<template>
  <div
    :class="['button', 'button--' + size, 'button--' + variant, circle && !text ? 'button--textOnlyCircle' : '', circle ? 'button--circle' : '', bottomBorder ? 'button--bottomBorder' : '', highlighted === true ? 'button--highlighted' : '', selected === true ? 'button--selected' : '', disabled === true ? 'button--disabled' : '', flipLayout === true ? 'button--flipped' : '', borderless === true ? 'button--borderless' : '']"
    @click="createRipple($event); disabled === false ? toggleButton(value) : ''"
  >
    <div
      v-if="icon"
      :class="['button__icon', 'button__icon--' + iconSize, 'button__icon--' + color, 'button__iconDefault--' + defaultColor, text ? 'button__icon--text' : '', iconReversed === true ? 'button__icon--reversed' : '', iconRotate !== false ? 'button__icon--rotate' + iconRotate : '']"
      v-html="icon"
    />

    <div
      v-if="text"
      :class="['button__text', textOnly ? 'button__text--only' : '', scaleText ? 'button__text--scale' : '', uppercaseText ? 'button__text--uppercase' : '', flipLayout === true ? 'button__text--flipped' : '', noPadding ? 'button__text--noPadding' : '', 'button__textColor--' + defaultColor]"
      v-html="text"
    />
  </div>
</template>

<script>
import helperMethods from '@/helpers/utility.js'

export default {
  props: {
    value: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'auto',
      validator (values) {
        return ['auto', 'sm', 'md'].includes(values)
      }
    },
    variant: {
      type: String,
      default: 'dark',
      validator (values) {
        return ['white', 'black', 'dark', 'light', 'header', 'register', 'login', 'initials', 'dotted'].includes(values)
      }
    },
    circle: {
      type: Boolean,
      default: false
    },
    noPadding: {
      type: Boolean,
      default: false
    },
    borderless: {
      type: Boolean,
      default: false
    },
    bottomBorder: {
      type: Boolean,
      default: false
    },
    icon: {
      type: [String, Boolean],
      default: false
    },
    iconReversed: {
      type: Boolean,
      default: false
    },
    iconRotate: {
      type: [Boolean, String],
      default: false,
      validator (values) {
        return [false, '90deg', '180deg', '270deg'].includes(values)
      }
    },
    flipLayout: {
      type: Boolean,
      default: false
    },
    iconSize: {
      type: String,
      default: 'md'
    },
    text: {
      type: [String, Number, Boolean],
      default: false
    },
    textOnly: {
      type: Boolean,
      default: false
    },
    scaleText: {
      type: Boolean,
      default: false
    },
    uppercaseText: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: 'blue'
    },
    defaultColor: {
      type: String,
      default: 'white'
    },
    textColor: {
      type: String,
      default: 'default'
    },
    highlighted: {
      type: Boolean,
      default: false
    },
    selected: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    toggleButton (value) {
      this.$emit('clicked', value)
    },
    ...helperMethods
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/app.scss';

.button {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  min-width: 22px;
  border-top: 1px solid rgba($white, 0.1);
  border-left: 1px solid rgba($white, 0.1);
  border-right: 1px solid rgba($black, 0.9);
  padding: 0px 8px;
  transition: background-color 0.2s ease-out;
  user-select: none;
  cursor: pointer;
  z-index: 4;

  @media screen and (min-width: $breakpointLg) {
    padding: 0px 10px;
  }

  &--textOnlyCircle {
    padding: 0;
  }

  &--auto {
    height: auto;
  }

  &--sm {
    height: 28px;

    @media screen and (min-width: $breakpointLg) {
      height: 30px;
    }
  }

  &--md {
    height: 34px;

    @media screen and (min-width: $breakpointLg) {
      height: 38px;
    }
  }

  &--black {
    background-color: $bg-1;

    .button__text {
      color: rgba($white, 0.7);
    }

    .button__icon {
      fill: rgba($white, 0.7);
    }
  }

  &--header {
    background-color: $bg-2;
    margin: auto;
  }

  &--dark {
    background-color: $bg-2;

    .button__text:not(.button__text--only) {
      color: rgba($white, 0.7);
    }

    .button__icon {
      fill: rgba($white, 0.7);
    }
  }

  &--light {
    background-color: $bg-3;

    .button__text {
      color: rgba($white, 0.7);
    }

    .button__icon {
      fill: rgba($white, 0.7);
    }
  }

  &--white {
    padding: 0px;

    &:hover {
      div {
        background-color: rgba($blue, 0.5);
      }
    }

    div {
      font-size: 14px;
      color: rgba($white, 1) !important;
      font-weight: 500;
      letter-spacing: 0.7px;
      border: 2px solid $white !important;
      border-radius: 999px !important;
      padding: 0 32px;
      transition: background-color 0.2s ease-out, color 0.2s ease-out, border 0.2s ease-out;

      @media screen and (min-width: $breakpointLg) {
        font-size: 15px;
      }
    }
  }

  &--register, &--login, &--initials {
    padding: 0px 0px 0px 4px;
    margin: auto;

    div {
      font-size: 14px;
      color: rgba($white, 0.8) !important;
      font-weight: 500;
      letter-spacing: 0.7px;
      border: 2px solid $bg-2 !important;
      border-radius: 999px !important;
      transition: background-color 0.2s ease-out, color 0.2s ease-out, border 0.2s ease-out;

      @media screen and (min-width: $breakpointLg) {
        font-size: 15px;
      }
    }
  }

  &--register {
    .button__text {
      color: $blue !important;
    }
  }

  &--register, &--login {
    div {
      padding: 2px 12px;
    }
  }

  &--initials {
    div {
      width: 28px;
      height: 28px;
      line-height: 28px;
      background-color: $bg-2;
      padding: 0px;
      text-align: center;

      @media screen and (min-width: $breakpointLg) {
        width: 30px;
        height: 30px;
        line-height: 30px;
      }
    }
  }

  &--register, &--login, &--initials {
    div {
      border: 2px solid $bg-1 !important;
    }
  }

  &--dotted {
    padding: 0px;

    &:hover {
      div {
        background-color: rgba($blue, 0.5);
      }
    }

    div {
      font-size: 14px;
      color: rgba($white, 1) !important;
      font-weight: 500;
      letter-spacing: 0.7px;
      border: 2px dotted rgba($white, 0.5) !important;
      border-radius: 999px !important;
      padding: 0 32px;
      transition: background-color 0.2s ease-out, color 0.2s ease-out, border 0.2s ease-out;

      @media screen and (min-width: $breakpointLg) {
        font-size: 15px;
      }
    }
  }

  &--circle {
    min-width: 28px;
    border-radius: 999px !important;
    border: 2px solid $bg-1 !important;

    .button__icon:not(.button--selected) {
      fill: rgba($white, 0.5);
    }

    /*&.button--sm {
      height: 28px !important;
    }

    &.button--md {
      height: 34px !important;
    }*/
  }

  &--borderless {
    border-left: none;
    border-right: none;
    border-top: none;
    border-bottom: none;
  }

  &--bottomBorder {
    border-top: 2px solid rgba($bg-2, 0);
    border-bottom: 2px solid rgba($bg-2, 0);

    &.button--selected {
      border-bottom: 2px solid $blue;

      .button__text {
        color: rgba($white, 0.9) !important;
      }
    }

    &:hover:not(.button--selected), &:active:not(.button--selected) {
      border-bottom: 2px solid rgba($white, 0.7);
    }
  }

  &--flipped {
    flex-direction: row-reverse;
  }

  .button__text--uppercase {
    text-transform: uppercase;
  }

  &--disabled {
    cursor: not-allowed;

    &:is(.button--selected) {
      background-color: $bg-1;
    }
    .button__icon {
      fill: rgba($white, 0.15) !important;
    }
    .button__text {
      color: rgba($white, 0.25) !important;
    }
  }

  &:hover:not(.button--disabled):not(.button--selected), &:active:not(.button--disabled):not(.button--selected) /*, &:focus:not(.button--disabled):not(.button--selected) */ {
    &:is(.button--header) {
      background-color: $bg-1;
    }
    
    &:is(.button--dark) {
      background-color: $bg-1;
    }

    &:is(.button--light) {
      background-color: $bg-1;
    }

    &:is(.button--register), &:is(.button--login), &:is(.button--initials) {
      div {
        background-color: $bg-1;
        border-color: $bg-1 !important;
      }

      .button__text {
        color: $blue !important;
      }
    }

    .button__text {
      color: $white;
    }

    .button__textColor--blue {
      color: $blue;
    }

    .button__icon, .button__text--scale {
      transform: scale(1.2);
      fill: $white;

      @media screen and (min-width: $breakpointLg) {
        transform: scale(1.175);
      }
    }

    .button__icon--reversed {
      transform: rotate(180deg) scale(1.2);

      @media screen and (min-width: $breakpointLg) {
        transform: rotate(180deg) scale(1.175);
      }
    }

    .button__icon--rotate90deg {
      transform: rotate(90deg) scale(1.2);

      @media screen and (min-width: $breakpointLg) {
        transform: rotate(90deg) scale(1.175);
      }
    }

    .button__icon--rotate180deg {
      transform: rotate(180deg) scale(1.2);

      @media screen and (min-width: $breakpointLg) {
        transform: rotate(180deg) scale(1.175);
      }
    }

    .button__icon--rotate270deg {
      transform: rotate(270deg) scale(1.2);

      @media screen and (min-width: $breakpointLg) {
        transform: rotate(270deg) scale(1.175);
      }
    }
  }

  &--selected:is(.button--black) {
    background-color: darken($bg-1, 2.5);

    .button__text {
      color: $blue;
    }
  }

  &--selected:is(.button--header) {
    background-color: $bg-1 !important;
  }

  &--selected:is(.button--dark) {
    background-color: $bg-1;

    .button__text {
      color: $blue;
    }
  }

  &--selected:is(.button--light) {
    background-color: darken($bg-2, 4);

    .button__text {
      color: $blue;
    }
  }

  &--highlighted:is(.button--header) {
    background-color: $bg-2;
    //border-color: rgba($blue, 0.75) !important;
  }

  /*&--selected:not(.button--disabled):not(.button--light):not(.button--black) {
    background-color: $bg-1;

    .button__text.button__text--noPadding {
      color: $blue;
    }
  }

  &--selected:not(.button--disabled):not(.button--dark):not(.button--black) {
    background-color: darken($bg-2, 4); //lighten($bg-2, 10); $bg-2?

    .button__text {
      color: $blue;
    }
  }*/

  &:active:not(.button--disabled) {
    //background-color: $bg-1 !important; TODO: Why was this important? Need it disabled for other reasons...

    .button__icon {
      fill: $blue !important;
    }

    .button__icon--reversed {
      transform: rotate(180deg) scale(1);
    }

    .button__icon--rotate90deg {
      transform: rotate(90deg) scale(1);
    }

    .button__icon--rotate180deg {
      transform: rotate(180deg) scale(1);
    }

    .button__icon--rotate270deg {
      transform: rotate(270deg) scale(1);
    }

    .button__text {
      color: $blue;
      transform: scale(1);
    }
  }

  &__text {
    @include font('content--md', $white, 0.7);
    padding: 0 0 0 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: hidden;

    @media screen and (min-width: $breakpointLg) {
      @include font('content--lg', $white, 0.7);
      padding: 0 0 0 6px;
    }

    &--only {
      padding: 0px 6px;
    }

    &--flipped {
      padding: 0 6px 0 0;
    }

    &--noPadding {
      padding: 0 !important;
    }
  }

  /*&__text-icon {
    @include font('content--md', $white, 0.5);
    padding: 0px 8px 0px 8px;
    display: none;

    @media screen and (min-width: $breakpointMd) {
      display: block;
    }

    @media screen and (min-width: $breakpointLg) {
      @include font('content--lg', $white, 0.5);
      padding: 0px 10px 0px 0px;
    }
  }*/

  &__icon {
    display: flex;
    fill: rgba($white, 0.7);
    transition: fill 0.2s ease-out, transform 0.2s ease-out;

    &Default {
      &--blue {
        fill: rgba($blue, 0.9) !important;
      }

      &--green {
        fill: rgba($green, 0.9) !important;
      }

      &--red {
        fill: rgba($red, 0.9) !important;
      }

      &--light {
        fill: rgba($white, 0.67) !important;
      }
    }

    &--text {
      //padding-left: 6px;
    }

    &--xxs {
      min-width: 12px;
      width: 12px;
      height: 12px;

      @media screen and (min-width: $breakpointLg) {
        min-width: 14px;
        width: 14px;
        height: 14px;
      }
    }

    &--xs {
      min-width: 14px;
      width: 14px;
      height: 14px;

      @media screen and (min-width: $breakpointLg) {
        min-width: 16px;
        width: 16px;
        height: 16px;
      }
    }

    &--sm {
      min-width: 16px;
      width: 16px;
      height: 16px;

      @media screen and (min-width: $breakpointLg) {
        min-width: 18px;
        width: 18px;
        height: 18px;
      }
    }

    &--md {
      min-width: 18px;
      width: 18px;
      height: 18px;

      @media screen and (min-width: $breakpointLg) {
        min-width: 20px;
        width: 20px;
        height: 20px;
      }
    }

    &--lg {
      min-width: 20px;
      width: 20px;
      height: 20px;

      @media screen and (min-width: $breakpointLg) {
        min-width: 20px;
        width: 22px;
        height: 22px;
      }
    }

    &--xl {
      min-width: 22px;
      width: 22px;
      height: 22px;

      @media screen and (min-width: $breakpointLg) {
        min-width: 24px;
        width: 24px;
        height: 24px;
      }
    }

    &--reversed {
      transform: rotate(180deg);
    }

    &--rotate90deg {
      transform: rotate(90deg);
    }

    &--rotate180deg {
      transform: rotate(180deg);
    }

    &--rotate270deg {
      transform: rotate(270deg);
    }

    .button--selected &--red {
      fill: $red !important;
    }

    .button--selected &--blue {
      fill: $blue !important;
    }

    .button--selected &--green {
      fill: $green !important;
    }

    .button--selected &--yellow {
      fill: $yellow !important;
    }

    .button--selected &--orange {
      fill: $orange !important;
    }

    .button--selected &--pink {
      fill: $pink !important;
    }
  }
}

</style>
